import { array, bool, object, objectOf, oneOfType, string } from 'prop-types';
import React from 'react';
import get from 'lodash/get';
import VisualNav from 'yoda-core-components/lib/components/VisualNav/VisualNav';
import { checkUrlExtension, replaceSubdomain } from 'yoda-core-components/lib/helpers/Utils/Utils';
import EditorialContent from '../EditorialContent/EditorialContent';
import Structure from '../Structure/Structure';
import Item from '../Structure/Item';
import ShopAllButton from '../ShopAllButton/ShopAllButton';
import {
    categoryDealsTheme,
    visNavThemes,
    editorialTypes,
    ctaPositionConst,
} from './CategoryDealsTheme';
import HeadingComponent from '../EditorialContent/HeadingComponent';

const mapEditorial = ({
    adobeAudience = '',
    backgroundColor = '',
    callToActionsGroupLabel = '',
    description,
    headline,
    media = {},
    primaryLink,
    seeDetailsLink,
    theme,
}) => ({
    adobeAudience,
    backgroundColor,
    callToActions: [],
    callToActionsGroupLabel,
    componentType: 'editorial',
    description,
    headline,
    media,
    primaryLink,
    seeDetailsLink,
    theme,
});

const mapVisNav = (content, preferences) => {
    const massagedData = [];
    /* istanbul ignore next */
    if (content && content.items) {
        content.items.forEach((value) => {
            let itemObj = {};
            const {
                linkText: title = '',
                media: imageUrl = '',
                link: linkUrl = '',
                analyticsTag = '',
                openInNewTab = false,
            } = value;
            let { url: image = '' } = imageUrl || {};
            const { url: links = '' } = linkUrl || {};
            const ext = checkUrlExtension(value.media.url);
            const { className = '' } = value.media;
            image = replaceSubdomain(image, preferences);
            itemObj = { title, image, links, analyticsTag, openInNewTab, ext, className };
            massagedData.push(itemObj);
        });
    }
    return massagedData;
};

const getSupportData = (props, editorialContent) => {
    const { style, componentType, deviceType } = props;
    let theme = {
        vizNavTheme: visNavThemes.styleA,
        editorialType: editorialTypes.editorialDealStyleA,
        styleTheme: categoryDealsTheme.styleATheme,
    };
    let commonProps = {
        title: {
            headline: '',
            description: '',
            seeDetailsLink: '',
            callToActions: [{}],
            primaryLink: {},
        },
    };
    let visNavProps = {};
    let headlineAbove = false;
    let ctaPosition = ctaPositionConst.bottom;
    /* istanbul ignore next */
    switch (componentType) {
        // headline-within,cta-top,cta-middle
        case 'categorypromo':
            if (editorialContent.theme) {
                headlineAbove = editorialContent.theme.large === 'headline-above';
                ctaPosition = editorialContent.theme.compact;
            }
            if (style === 'stylea') {
                theme = {
                    vizNavTheme: visNavThemes.styleA4Cat,
                    editorialType: editorialTypes.editorialDealStyleA,
                    styleTheme: categoryDealsTheme.styleA4Cat,
                    ctaPosition,
                };
            } else if (style === 'styleb') {
                theme = {
                    vizNavTheme: visNavThemes.styleA6Cat,
                    editorialType: editorialTypes.editorialDealStyleA6Cat,
                    styleTheme: categoryDealsTheme.styleA6Cat,
                    ctaPosition,
                };
            } else if (style === 'stylec') {
                theme = {
                    vizNavTheme: visNavThemes.styleA8Cat,
                    editorialType: editorialTypes.editorialDealStyleA8Cat,
                    styleTheme: categoryDealsTheme.styleA8Cat,
                    ctaPosition,
                };
                visNavProps = {
                    deviceType,
                    itemsPerRow: 4,
                    direction: 'Wrap',
                };
            } else {
                theme = {
                    vizNavTheme: visNavThemes.styleA,
                    editorialType: editorialTypes.editorialDealStyleA,
                    styleTheme: categoryDealsTheme.styleATheme,
                };
            }
            break;
        case 'categoryhotdeals':
            if (editorialContent.theme) {
                const {
                    headline,
                    description,
                    seeDetailsLink,
                    callToActions,
                    primaryLink,
                } = editorialContent;
                headlineAbove = editorialContent.theme.large === 'styleb';
                if (headlineAbove) {
                    theme = {
                        vizNavTheme: visNavThemes.styleB,
                        editorialType: editorialTypes.editorialDealStyleB,
                        styleTheme: categoryDealsTheme.styleBTheme,
                    };
                    commonProps = {
                        title: {
                            headline,
                            description,
                            seeDetailsLink,
                            callToActions,
                            primaryLink,
                        },
                    };
                    visNavProps = {
                        enableCarouselSlider: true,
                        enableShopAllBtn: true,
                        deviceType,
                    };
                } else if (editorialContent.theme.large === 'stylec') {
                    theme = {
                        vizNavTheme: visNavThemes.styleC,
                        editorialType: editorialTypes.editorialDealStyleC,
                        styleTheme: categoryDealsTheme.styleCTheme,
                    };
                    visNavProps = {
                        enableCarouselSlider: true,
                        enableShopAllBtn: true,
                        deviceType,
                    };
                }
            }
            break;
        default:
            break;
    }
    return { theme, commonProps, visNavProps, headlineAbove };
};

const getFormatedShopAllData = (data) => {
    /* istanbul ignore next */
    const { analyticsTag = '', link = {}, linkText = '' } = data || {};
    const { url: links = '', openInNewTab = '' } = link || {};
    /* istanbul ignore next */
    if (!linkText || !links) {
        return null;
    }
    /* istanbul ignore next */
    return {
        analyticsTag,
        links,
        openInNewTab,
        title: linkText,
    };
};

const CategoryDeals = (props) => {
    const { adobeAudience = '', deviceType = {}, preferences = {}, componentType = '' } = props;
    const editorialContent = mapEditorial(props);
    const visNavContent = mapVisNav(props, preferences);
    const { theme, commonProps, visNavProps, headlineAbove } = getSupportData(
        props,
        editorialContent
    );
    const vizNavTheme = theme && theme.vizNavTheme;
    const primaryLink = get(props, 'primaryLink', {});
    const ctaPosition = get(theme, 'ctaPosition', ctaPositionConst.bottom);
    editorialContent.callToActions = [primaryLink];
    const shopAllData = getFormatedShopAllData(primaryLink);
    const automationIDTheme = `structure-${theme.styleTheme}`;
    /* istanbul ignore next */
    const showCtaButton = ctaPosition ? ctaPosition === ctaPositionConst.bottom : true;

    return (
        <Structure
            automationId={automationIDTheme}
            theme={theme.structureTheme}
            {...commonProps}
            dataItem={primaryLink}
            styleTheme={theme.styleTheme}
            showCtaButton={showCtaButton}
            {...categoryDealsTheme}
        >
            {
                /* istanbul ignore next */
                headlineAbove && (
                    <Item containerWidth={categoryDealsTheme.containerTitle}>
                        <HeadingComponent
                            headlineAboveClass={categoryDealsTheme.headlineAboveClass}
                            {...editorialContent}
                            headlineAbove
                        />
                    </Item>
                )
            }
            {
                /* istanbul ignore next */
                ctaPosition === ctaPositionConst.top && deviceType.isMobile ? (
                    <Item
                        containerWidth={categoryDealsTheme.containerTitle}
                        containerClass={categoryDealsTheme.headlineAboveClass}
                    >
                        <ShopAllButton dataItem={primaryLink} buttonType="Link" classname />
                    </Item>
                ) : null
            }
            <Item
                containerClass={categoryDealsTheme.container}
                containerWidth={categoryDealsTheme.container1}
            >
                <EditorialContent
                    {...editorialContent}
                    deviceType={deviceType}
                    adobeAudience={adobeAudience}
                    componentType={theme.editorialType}
                    preferences={preferences}
                    headlineAbove={headlineAbove}
                    ctaPosition={ctaPosition}
                />
            </Item>
            <Item
                containerClass={categoryDealsTheme.container}
                containerWidth={categoryDealsTheme.container2}
            >
                <VisualNav
                    enableCarouselSlider
                    {...visNavProps}
                    adobeAudience={adobeAudience}
                    customTheme={theme.vizNavTheme}
                    vizNavTheme={vizNavTheme}
                    datasource={visNavContent}
                    shopAllData={shopAllData}
                    isCategoryPromo={componentType === 'categorypromo'}
                />
            </Item>
        </Structure>
    );
};

CategoryDeals.propTypes = {
    adobeAudience: string,
    componentType: string,
    content: object,
    deviceType: objectOf(bool),
    preferences: oneOfType([array, object]),
};

CategoryDeals.defaultProps = {
    adobeAudience: '',
    componentType: '',
    content: {},
    deviceType: {},
    preferences: {},
};

export default CategoryDeals;
